import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PrinterIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { RootState } from '../store/store';

const ReceiptPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Try to get order from Redux state first (from voice order flow)
  const stateOrder = useSelector((state: RootState) => state.voice.currentOrder);

  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If we have the order in state and it matches the orderId, use it
    if (stateOrder && (stateOrder.orderNumber === orderId || stateOrder.id === orderId)) {
      setOrder(stateOrder);
      setLoading(false);
    } else if (orderId) {
      // Otherwise fetch from API
      fetchOrderDetails(orderId);
    } else {
      setLoading(false);
    }
  }, [orderId, stateOrder]);

  // Auto-print if ?print=true parameter is present
  useEffect(() => {
    if (order && searchParams.get('print') === 'true') {
      // Small delay to ensure content is loaded
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [order, searchParams]);

  const fetchOrderDetails = async (orderNumber: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/method/webvoiceagent.webvoiceagent.api.voice_api.get_order_details?order_id=${orderNumber}`
      );

      if (response.ok) {
        const result = await response.json();
        setOrder(result.message?.data || result.data);
      }
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Receipt Not Found</h1>
          <p className="text-gray-600 mb-6">
            We couldn't find the receipt you're looking for.
          </p>
          <Link
            to="/voice-order"
            className="inline-block bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Back to Voice Order
          </Link>
        </div>
      </div>
    );
  }

  const orderDate = new Date(order.createdAt);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Print-hidden controls */}
        <div className="mb-6 flex items-center justify-between print:hidden">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back</span>
          </button>

          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <PrinterIcon className="w-5 h-5" />
            <span>Print Receipt</span>
          </button>
        </div>

        {/* Receipt Container */}
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 print:shadow-none">
          {/* Header */}
          <div className="text-center border-b-2 border-gray-200 pb-6 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              🍽️ Restaurant Receipt
            </h1>
            <p className="text-lg text-gray-600">Voice Order System</p>
            <div className="mt-4 text-sm text-gray-500">
              <p>123 Restaurant Street, City</p>
              <p>Phone: (555) 123-VOICE</p>
              <p>Email: info@restaurant.com</p>
            </div>
          </div>

          {/* Order Info */}
          <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <p className="text-sm text-gray-500 mb-1">Receipt Number</p>
              <p className="font-semibold text-gray-900">{order.orderNumber || order.id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Date & Time</p>
              <p className="font-semibold text-gray-900">
                {orderDate.toLocaleDateString()} {orderDate.toLocaleTimeString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Order Type</p>
              <p className="font-semibold text-gray-900 capitalize">{order.orderType || 'Dine-in'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Payment Method</p>
              <p className="font-semibold text-gray-900">{order.paymentMethod || 'Cash'}</p>
            </div>
          </div>

          {/* Customer Info */}
          {order.customer && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Name:</span> <span className="font-medium">{order.customer.name}</span></p>
                {order.customer.phone && (
                  <p><span className="text-gray-500">Phone:</span> <span className="font-medium">{order.customer.phone}</span></p>
                )}
                {order.customer.email && (
                  <p><span className="text-gray-500">Email:</span> <span className="font-medium">{order.customer.email}</span></p>
                )}
              </div>
            </div>
          )}

          {/* Items */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-2 text-sm font-semibold text-gray-700">Item</th>
                  <th className="text-center py-2 text-sm font-semibold text-gray-700">Qty</th>
                  <th className="text-right py-2 text-sm font-semibold text-gray-700">Price</th>
                  <th className="text-right py-2 text-sm font-semibold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item: any, index: number) => {
                  const itemTotal = (item.quantity || 1) * (item.rate || 0);
                  return (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-3 text-gray-900">{item.itemName}</td>
                      <td className="py-3 text-center text-gray-900">{item.quantity || 1}</td>
                      <td className="py-3 text-right text-gray-900">£{item.rate.toFixed(2)}</td>
                      <td className="py-3 text-right font-medium text-gray-900">£{itemTotal.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="border-t-2 border-gray-300 pt-4">
            <div className="space-y-2">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal:</span>
                <span>£{order.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Tax (Included):</span>
                <span>£0.00</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t border-gray-300">
                <span>Total:</span>
                <span>£{order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-lg font-semibold text-green-600 mb-2">✓ Payment Received</p>
            <p className="text-sm text-gray-500 mb-4">Thank you for your order!</p>
            <div className="text-xs text-gray-400">
              <p>This is an official receipt for your order</p>
              <p className="mt-1">For support: support@restaurant.com or (555) 123-VOICE</p>
            </div>
          </div>

          {/* QR Code or Barcode placeholder */}
          <div className="mt-6 text-center print:block hidden">
            <div className="inline-block border-2 border-gray-300 p-4">
              <p className="text-xs text-gray-500 mb-2">Order #{order.orderNumber || order.id}</p>
              <div className="w-32 h-32 bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                QR Code
              </div>
            </div>
          </div>
        </div>

        {/* Additional Actions (print-hidden) */}
        <div className="mt-6 text-center print:hidden">
          <Link
            to="/voice-order"
            className="inline-block text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            Place Another Order
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPage;
